<template>
  <div class="main-container">
    <div class="d-flex justify-content-between align-items-center">
      <h1 class="mb-0">{{ $t("bop.title") }}</h1>
      <div id="buttonHeader">
        <b-button
          variant="secondary"
          class="btn-login btn-append"
          @click="openExportModal"
        >
          <span>{{ $t("home.export") }}</span>
        </b-button>
        <router-link :to="{ path: 'bops/add' }">
          <b-button
            variant="primary"
            class="btn-login btn-append"
          >
            <span>{{ $t("home.createBop") }}</span>
            <div>
              <b-icon
                icon="arrow-right"
                aria-hidden="true"
              ></b-icon>
            </div>
          </b-button>
        </router-link>
      </div>
    </div>
    <b-card class="default mb-3">
      <b-row class="align-items-end">
        <b-col md="4">
          <b-form-group
            :key="$i18n.locale"
            id="type-group"
            :label="$t('home.selectStatuses')"
            label-for="selectElementType-select"
          >
            <multiselect
              id="selectElementType-select"
              v-model="selectedStatuses"
              :options="elementStatuses"
              :multiple="true"
              label="text"
              track-by="value"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :preselect-first="false"
              :showLabels="false"
              placeholder=""
              :showNoResults="false"
              @input="handleFilterChange"
            >
              <span slot="noResult">...</span>
            </multiselect>
          </b-form-group>
        </b-col>
        <b-col
          md="6"
          class="d-flex align-items-end"
        >
          <div
            class="table-search d-flex flex-grow-1"
            style="min-width: 520px; height: 38px"
          >
            <b-input-group
              size="md"
              class="h-100"
            >
              <b-input-group-prepend
                is-text
                class="h-100"
              >
                <b-icon
                  icon="search"
                  color="#003478"
                ></b-icon>
              </b-input-group-prepend>
              <b-form-input
                id="bop-search-input"
                type="search"
                v-model="searchValue"
                :placeholder="$t('bopModelSetup.filter').toString()"
                @input="onSearchInput"
                @keyup.enter="commitSearch"
                class="h-100"
              ></b-form-input>
            </b-input-group>
            <b-button
              variant="secondary"
              class="btn-append ml-2"
              @click="resetFilters"
            >
              <span>{{ $t("home.resetFilters") }}</span>
              <div>
                <b-icon
                  icon="arrow-clockwise"
                  aria-hidden="true"
                ></b-icon>
              </div>
            </b-button>
          </div>
        </b-col>
        <b-col md="4">
          <b-form-group
            id="site-group"
            :label="$t('bop.bopModelName')"
            label-for="selectBopModel-select"
          >
            <b-form-select
              id="selectBopModel-select"
              v-model="selectedBopModel"
              :options="bopModels"
              @change="handleFilterChange"
            >
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <div class="d-flex align-items-center flex-grow-1">
            <b-form-group
              style="width: 100%"
              :label="$t('bopSetup.createdDate')"
              label-for="bopCreatedDate"
            >
              <flat-pickr
                id="date-range"
                v-model="selectedDateRange"
                :config="datePickerConfig"
                @on-close="onDateRangeClose"
              />
            </b-form-group>
          </div>
        </b-col>
      </b-row>
    </b-card>
    <template v-if="url.length">
      <DataTable
        :columns="dtColumns"
        :url="url"
        :key="url"
        trackBy="id"
        :serverMode="true"
        :perPage="20"
        :searchable="false"
        :parseResponse="parseBopResponse"
        @row-clicked="onRowClicked"
        ref="table"
        :isRowClickable="true"
      >
        <template v-slot:statusCell="{ row }">
          <StatusField
            :props="{
              rowData: row,
              rowField: { property: 'bopStatusName', isNotEditable: true },
            }"
          />
        </template>
        <template v-slot:createdDateCell="{ row }">
          <EditField
            :props="{
              rowData: row,
              rowField: {
                property: 'createdDate',
                isNotEditable: true,
                isDate: true,
              },
            }"
          />
        </template>
        <template v-slot:lastSignatureDateCell="{ row }">
          <EditField
            :props="{
              rowData: row,
              rowField: {
                property: 'lastSignatureDate',
                isNotEditable: true,
                isDate: true,
              },
            }"
          />
        </template>
        <template v-slot:initDismantleMailDateCell="{ row }">
          <EditField
            :props="{
              rowData: row,
              rowField: {
                property: 'initDismantleMailDate',
                isNotEditable: true,
                isDate: true,
              },
            }"
          />
        </template>
        <template v-slot:immobilizationDaysCell="{ row }">
          <span class="d-inline-block py-2">{{
            calculateImmobilizationDays(
              row.initDismantleMailDate,
              row.lastSignatureDate,
              row.dismantleDoneDate,
            )
          }}</span>
        </template>
        <template v-slot:dismantleDoneDateCell="{ row }">
          <EditField
            v-if="!isDismantleDoneDateExpected(row)"
            :props="{
              rowData: row,
              rowField: {
                property: 'dismantleDoneDate',
                isNotEditable: true,
                isDate: true,
              },
            }"
          />
          <p
            v-else
            class="h3"
            style="margin: 9px 0"
          >
            <b-icon-exclamation-circle-fill
              style="color: #e30040 !important"
              v-b-tooltip.hover.bottom="$t('bop.dismantleDoneDateAlert')"
            />
          </p>
        </template>
        <template v-slot:header-bopStatusKey>
          <b-button
            variant="outline-secondary"
            class="btn-column-selector"
            @click.stop="showColumnSelector = true"
            :title="$t('bop.columnSelector')"
          >
            <b-icon
              icon="gear-fill"
              aria-hidden="true"
            ></b-icon>
          </b-button>
        </template>
        <template v-slot:isPermanentCell="{ row }">
          <span class="d-inline-block py-2">{{
            row.isPermanent
              ? $t("bopSetup.feb.isPermanentYes")
              : $t("bopSetup.feb.isPermanentNo")
          }}</span>
        </template>
        <template v-slot:actionBopAccueilCell="{ row }">
          <ActionBopAccueilField
            :props="{
              rowData: row,
              rowField: {
                property: 'bopStatusKey',
                isNotEditable: true,
                isAction: true,
                sendMail: true,
                transformBop: true,
              },
            }"
            @open-dismantle="toggleDismantleModal($event)"
          />
        </template>
      </DataTable>
    </template>
    <DismantleModal
      v-if="showDismantleModal"
      :show="showDismantleModal"
      :validateAt="computedDismantleDoneMinDate"
      :bopCreationDate="selectedRow.createdDate"
      @close="toggleDismantleModal"
      @dismantle="saveNewDismantleDoneDate($event)"
    />
    <ExportBopModal
      :show="showExportModal"
      @close="closeExportModal"
      @validate="handleExportValidate"
    />
    <ColumnSelectorModal
      :show="showColumnSelector"
      :columns="selectableColumns"
      :hiddenColumns.sync="hiddenColumns"
      @close="showColumnSelector = false"
    />
  </div>
</template>
<script lang="ts">
import Vue from "vue";
import BopService from "@/services/BopService";
import UserService from "@/services/UserService";
import User from "@/models/User";
import { BopModelEnum, BopStatus } from "@/utils/Enums";
import BopStatusService from "@/services/BopStatusService";
import BopModelService from "@/services/BopModelService";
import HomeFilter from "@/models/HomeFilter";
import AdminExport from "@/models/AdminExport";
import ExportService from "@/services/ExportService";
import Constants from "@/utils/Constants";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { French } from "flatpickr/dist/l10n/fr.js";
import DismantleModal from "@/components/BopSetup/DismantleModal.vue";
import { diffDays } from "@/utils/helpers";

document.title = "Arkema Touch";

type Data = {
  columns: Array<any>;
  rows: Array<any>;
};

export default Vue.extend({
  name: "Bop",
  components: {
    DataTable: () => import("@/components/Shared/Table/DataTable.vue"),
    StatusField: () => import("@/components/Shared/Table/StatusField.vue"),
    EditField: () => import("@/components/Shared/Table/EditField.vue"),
    ActionBopAccueilField: () =>
      import("@/components/Shared/Table/ActionBopAccueilField.vue"),
    ExportBopModal: () => import("@/components/Bop/ExportBopModal.vue"),
    ColumnSelectorModal: () =>
      import("@/components/Bop/ColumnSelectorModal.vue"),
    flatPickr,
    DismantleModal: () => import("@/components/BopSetup/DismantleModal.vue"),
  },
  data() {
    return {
      url: "",
      data: { columns: [], rows: [] } as Data,
      elementStatuses: [] as any[],
      selectedStatuses: [] as any[],
      bopModels: [] as any[],
      selectedBopModel: "",
      searchValue: "",
      currentUser: UserService.getCurrentuser() as User,
      searchTimer: null as any,
      showExportModal: false,
      showColumnSelector: false,
      hiddenColumns: [] as string[],
      selectedAdminExport: null as AdminExport | null,
      selectedDateRange: null as Date[] | null,
      startDateFilter: "",
      endDateFilter: "",
      selectedRow: null as any,
      showDismantleModal: false,
    };
  },
  computed: {
    language(): string {
      return (this as any).$i18n.locale;
    },
    selectableColumns(): Array<{ property: string; title: string }> {
      const nonHideable = [
        "actionBopAccueil",
        "createdDate",
        "bopStatusName",
        "bopModelName",
        "bopStatusKey",
      ];
      return ((this as any).data.columns || [])
        .filter((c: any) => !nonHideable.includes(c.property || c.name))
        .map((c: any) => ({
          property: c.property || c.name,
          title: c.title,
        }));
    },
    dtColumns(): Array<any> {
      const hidden = new Set(this.hiddenColumns);
      return ((this as any).data.columns || [])
        .filter((c: any) => !hidden.has(c.property || c.name))
        .map((c: any) => {
          const property = c.property || c.name;
          const dateProps = [
            "createdDate",
            "lastSignatureDate",
            "initDismantleMailDate",
            "dismantleDoneDate",
          ];
          let slot: string | undefined = undefined;
          if (c.name === "status") slot = "statusCell";
          else if (c.name === "actionBopAccueil") slot = "actionBopAccueilCell";
          else if (property === "immobilizationDays")
            slot = "immobilizationDaysCell";
          else if (property === "isPermanent") slot = "isPermanentCell";
          else if (dateProps.includes(property)) slot = property + "Cell";
          return {
            key: property,
            label: c.title,
            sortField: c.sortField || (c.property ? c.property : undefined),
            slot,
            sortable: c.sortable,
          };
        });
    },
    isRowClickTemporaryDisabled(): boolean {
      return this.$store.state.isRowClickTemporaryDisabled;
    },
    datePickerConfig(): Record<string, any> {
      return {
        mode: "range",
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: this.$i18n.locale === "fr" ? "l j F Y" : "l, F j, Y",
        locale: this.$i18n.locale === "fr" ? French : undefined,
        allowInput: false,
        altInputClass: "form-control",
      };
    },
    computedDismantleDoneMinDate(): Date {
      return this.selectedRow.lastSignatureDate;
    },
  },
  methods: {
    modify(data: any) {
      const key = data.data.bopStatusKey;
      const model = data.data.bopModelKey;
      const hasFebSignature = data.data.hasFebSignature;
      const isSapCreated = data.data.isCreatedBySap;
      switch (key) {
        case BopStatus[BopStatus.Initial]:
          (this as any).$router.push({
            name: "BopSetupUpdate",
            params: { bopId: data.data.id },
            query: { index: "0" },
          });
          break;
        case BopStatus[BopStatus.WaitingForValidation]:
          if (model === BopModelEnum[BopModelEnum.Echafaudage]) {
            if (!hasFebSignature) {
              (this as any).$router.push({
                name: "BopSetupUpdate",
                params: { bopId: data.data.id },
                query: { index: "1" },
              });
            } else {
              (this as any).$router.push({
                name: "BopSetupUpdate",
                params: { bopId: data.data.id },
                query: { index: "5" },
              });
            }
          } else {
            (this as any).$router.push({
              name: "BopSetupUpdate",
              params: { bopId: data.data.id },
              query: { index: "4" },
            });
          }
          break;
        case BopStatus[BopStatus.AwaitingLaunchWorkApproval]:
          if (model === BopModelEnum[BopModelEnum.Echafaudage]) {
            if (!hasFebSignature) {
              (this as any).$router.push({
                name: "BopSetupUpdate",
                params: { bopId: data.data.id },
                query: { index: "1" },
              });
            } else {
              (this as any).$router.push({
                name: "BopSetupUpdate",
                params: { bopId: data.data.id },
                query: { index: "6" },
              });
            }
          } else {
            (this as any).$router.push({
              name: "BopSetupUpdate",
              params: { bopId: data.data.id },
              query: { index: "5" },
            });
          }
          break;
        case BopStatus[BopStatus.AcceptLaunchWork]:
          if (model === BopModelEnum[BopModelEnum.Echafaudage]) {
            if (!hasFebSignature) {
              (this as any).$router.push({
                name: "BopSetupUpdate",
                params: { bopId: data.data.id },
                query: { index: "1" },
              });
            } else {
              (this as any).$router.push({
                name: "BopSetupUpdate",
                params: { bopId: data.data.id },
                query: { index: "6" },
              });
            }
          } else {
            (this as any).$router.push({
              name: "BopSetupUpdate",
              params: { bopId: data.data.id },
              query: { index: "5" },
            });
          }
          break;
        case BopStatus[BopStatus.Finished]:
          if (model === BopModelEnum[BopModelEnum.Echafaudage]) {
            if (isSapCreated) {
              (this as any).$router.push({
                name: "BopSetupUpdate",
                params: { bopId: data.data.id },
                query: { index: "7", isEdit: "0" },
              });
            } else {
              (this as any).$router.push({
                name: "BopSetupUpdate",
                params: { bopId: data.data.id },
                query: { index: "6", isEdit: "0" },
              });
            }
          } else {
            if (isSapCreated) {
              (this as any).$router.push({
                name: "BopSetupUpdate",
                params: { bopId: data.data.id },
                query: { index: "6", isEdit: "0" },
              });
            } else {
              (this as any).$router.push({
                name: "BopSetupUpdate",
                params: { bopId: data.data.id },
                query: { index: "5", isEdit: "0" },
              });
            }
          }
          break;
        default:
          (this as any).$router.push({
            name: "BopSetupUpdate",
            params: { bopId: data.data.id },
            query: { index: "0" },
          });
      }
    },
    async loadElementStatuses() {
      const bopStatuses = (
        await BopStatusService.getAllStatuses((this as any).language)
      ).data;
      (this as any).elementStatuses = Object.keys(bopStatuses).map((key) => ({
        text: bopStatuses[key],
        value: key,
      }));
    },
    async loadBopTypes() {
      const bopTypes = (
        await BopModelService.getBopModels((this as any).language)
      ).data;
      (this as any).bopModels = Object.keys(bopTypes).map((key) => ({
        text: bopTypes[key].translation.name,
        value: bopTypes[key].bopModel.key,
      }));
    },
    parseBopResponse(resp: any): { rows: any[]; total: number } {
      const d = resp?.data;
      if (!d) return { rows: [], total: 0 };
      const rows: any[] = Array.isArray(d.data)
        ? d.data
        : Array.isArray(d.rows)
          ? d.rows
          : [];
      const total = d.pagination?.total || d.totalCount || rows.length;
      return { rows, total };
    },
    onRowClicked(row: any) {
      if (!this.isRowClickTemporaryDisabled) {
        this.modify({ data: row });
      } else {
        this.$store.commit("changeIsRowClickTemporaryDisabled", false);
      }
    },
    async loadData() {
      const preferences: HomeFilter = (
        await UserService.getUserPreferences((this as any).currentUser.id)
      ).data;
      if (preferences.selectedStatuses?.length > 0) {
        (this as any).selectedStatuses = preferences.selectedStatuses
          .filter((s) => s != "")
          .map((status) => ({
            text: (this as any).elementStatuses.find(
              (el: any) => el.value === status,
            )?.text,
            value: status,
          }));
      }
      (this as any).selectedBopModel = preferences.bopModel ?? null;
      (this as any).searchValue = preferences.search;
      this.hiddenColumns = preferences.hiddenColumns ?? [];

      this.startDateFilter = preferences.startDate || "";
      this.endDateFilter = preferences.endDate || "";
      if (preferences.startDate && preferences.endDate) {
        this.selectedDateRange = [
          new Date(preferences.startDate),
          new Date(preferences.endDate),
        ];
      }

      this.handleFilterChange();
      const data: Data = {
        columns: [
          {
            dataClass: "bop",
            name: "editable",
            sortField: "name",
            title: (this as any).$t("bop.name").toString(),
            property: "name",
            isNotEditable: true,
          },
          {
            name: "editable",
            sortField: "otNumber",
            title: (this as any).$t("bop.otNumber").toString(),
            property: "otNumber",
            isNotEditable: true,
          },
          {
            name: "editable",
            sortField: "idFss",
            title: (this as any).$t("bop.idFss").toString(),
            property: "idFss",
            isNotEditable: true,
            dataClass: "text-center",
          },
          {
            name: "editable",
            sortField: "bopNumber",
            title: (this as any).$t("bop.number").toString(),
            property: "bopNumber",
            isNotEditable: true,
          },
          {
            name: "status",
            sortField: "bopStatusName",
            title: (this as any).$t("bop.status").toString(),
            property: "bopStatusName",
            isNotEditable: true,
          },
          {
            name: "editable",
            sortField: "createdDate",
            title: (this as any).$t("bop.createdDate").toString(),
            property: "createdDate",
            isNotEditable: true,
            isDate: true,
          },
          {
            name: "editable",
            sortField: "lastSignatureDate",
            property: "lastSignatureDate",
            title: (this as any).$t("bop.lastSignatureDate").toString(),
            isDate: true,
            isNotEditable: true,
          },
          {
            name: "editable",
            sortField: "initDismantleMailDate",
            title: (this as any).$t("bop.initDismantleMailDate").toString(),
            property: "initDismantleMailDate",
            isDate: true,
            isNotEditable: true,
          },
          {
            name: "editable",
            sortField: "immobilizationDays",
            title: (this as any).$t("bop.immobilizationDays.title").toString(),
            property: "immobilizationDays",
            isNotEditable: true,
          },
          {
            name: "editable",
            sortField: "dismantleDoneDate",
            title: (this as any).$t("bop.dismantleDoneDate").toString(),
            property: "dismantleDoneDate",
            isDate: true,
            isNotEditable: true,
          },
          {
            name: "editable",
            sortField: "isPermanent",
            title: (this as any).$t("bop.permanentScaffold").toString(),
            property: "isPermanent",
            isNotEditable: true,
          },
          {
            name: "editable",
            sortField: "orderGiverUserFullName",
            title: (this as any).$t("bop.orderGiver").toString(),
            property: "orderGiverUserFullName",
            isNotEditable: true,
          },
          {
            name: "editable",
            sortField: "bopModelName",
            title: (this as any).$t("bop.bopModelName").toString(),
            property: "bopModelName",
            isNotEditable: true,
          },
          {
            name: "editable",
            sortField: "typeTravauxName",
            title: (this as any).$t("bopSetup.typeTravaux").toString(),
            property: "typeTravauxName",
            isNotEditable: true,
          },
          {
            name: "editable",
            sortField: "siteName",
            title: (this as any).$t("bop.siteName").toString(),
            property: "siteName",
            isNotEditable: true,
          },
          {
            name: "editable",
            sortField: "totalPoints",
            title: (this as any).$t("bop.totalPoints").toString(),
            property: "totalPoints",
            isNotEditable: true,
          },
          {
            name: "actionBopAccueil",
            title: "",
            property: "bopStatusKey",
            isNotEditable: true,
            isAction: true,
            sendMail: true,
            transformBop: true,
            sortable: false,
          },
        ],
        rows: [],
      };
      (this as any).data = data;
    },
    async handleChangeSearch(searchValue: string) {
      (this as any).searchValue = searchValue;
      const homeFilters = new HomeFilter();
      homeFilters.search = searchValue;
      homeFilters.selectedStatuses = (this as any).selectedStatuses.map(
        (status: any) => status.value,
      );
      homeFilters.hiddenColumns = this.hiddenColumns;
      await UserService.setPreferences(
        (this as any).currentUser.id,
        homeFilters,
      );
    },
    async savePreferences() {
      const homeFilters = new HomeFilter();
      homeFilters.search = (this as any).searchValue;
      homeFilters.selectedStatuses = (this as any).selectedStatuses.map(
        (status: any) => status.value,
      );
      homeFilters.bopModel = this.selectedBopModel;
      homeFilters.startDate = this.startDateFilter;
      homeFilters.endDate = this.endDateFilter;
      homeFilters.hiddenColumns = this.hiddenColumns;
      await UserService.setPreferences(
        (this as any).currentUser.id,
        homeFilters,
      );
    },
    handleFilterChange() {
      let bopModel = this.selectedBopModel ?? "";

      let statuses = "";
      if (this.selectedStatuses && (this as any).selectedStatuses.length > 0) {
        statuses = (this as any).selectedStatuses
          .map((status: any) => status.value)
          .join(",");
      }

      (this as any).url = BopService.getAllBopUrl(
        (this as any).language,
        (this as any).searchValue,
        statuses,
        bopModel,
        this.startDateFilter,
        this.endDateFilter,
        this.hiddenColumns.join(","),
      );
      this.refreshTable();
    },
    async resetFilters() {
      (this as any).selectedStatuses = [];
      (this as any).selectedDateRange = null;
      (this as any).searchValue = "";
      (this as any).selectedBopModel = "";
      (this as any).startDateFilter = "";
      (this as any).endDateFilter = "";
      const base = BopService.getAllBopUrl(
        (this as any).language,
        "",
        "",
        "",
        "",
        "",
        "",
      );
      (this as any).url = "";
      await (this as any).$nextTick();
      (this as any).url = base + `&t=${Date.now()}`;
      this.refreshTable();
    },
    refreshTable() {
      this.$nextTick(() => {
        const table: any = this.$refs.table;
        if (table && table.api && this.url) {
          table.api.load(this.url);
        }
      });
    },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.commitSearch();
      }, 400); // debounce
    },
    openExportModal() {
      (this as any).showExportModal = true;
    },
    closeExportModal() {
      (this as any).showExportModal = false;
    },
    async handleExportValidate(adminExport: AdminExport) {
      (this as any).selectedAdminExport = adminExport;
      (this as any).showExportModal = false;

      const statuses =
        (this as any).selectedStatuses.length > 0
          ? (this as any).selectedStatuses
              .map((status: any) => status.value)
              .join(",")
          : "";

      this.$store.commit("changeShowExportLoader", true);

      try {
        await ExportService.downloadBops(adminExport.id, adminExport.name);
      } catch (error: any) {
        this.$notify({
          group: "globalError",
          type: "error",
          title: this.$t("bop.exportFailed").toString(),
          text: error?.message || "Unable to download export file.",
          duration: Constants.DEFAULT_WAIT_ERROR_NOTIFICATIONS,
        });
      } finally {
        this.$store.commit("changeShowExportLoader", false);
      }
    },
    async commitSearch() {
      await this.handleChangeSearch((this as any).searchValue);
      this.handleFilterChange();
    },
    onDateRangeClose(selectedDates: Date[]) {
      const formatDateLocal = (dateObj: Date) => {
        const yyyy = dateObj.getFullYear();
        const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
        const dd = String(dateObj.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      };

      this.startDateFilter = selectedDates[0]
        ? formatDateLocal(selectedDates[0])
        : "";
      this.endDateFilter = selectedDates[1]
        ? formatDateLocal(selectedDates[1])
        : this.startDateFilter;

      this.handleFilterChange();
    },
    isDismantleDoneDateExpected(row: any): boolean {
      if (
        row.hasFebSignature &&
        row.initDismantleMailDate &&
        !row.dismantleDoneDate &&
        row.bopStatusKey !== BopStatus.Declined
      ) {
        return true;
      }
      return false;
    },
    toggleDismantleModal(row: any | null) {
      if (row) this.selectedRow = row;
      this.showDismantleModal = !this.showDismantleModal;
    },
    async saveNewDismantleDoneDate(newDate: Date) {
      if (!this.selectedRow.idFeb) return;

      try {
        await BopService.patchDismantleDate(this.selectedRow.idFeb, newDate);

        this.selectedRow.dismantleDoneDate = newDate.toString();

        this.$notify({
          group: "bopNotificationGroup",
          type: "success",
          title: this.$t("saveDismantleDoneDate.success.title").toString(),
          text: this.$t("saveDismantleDoneDate.success.message").toString(),
        });
      } catch (error) {
        this.$notify({
          group: "globalError",
          type: "error",
          title: this.$t("saveDismantleDoneDate.error.title").toString(),
          text: this.$t("saveDismantleDoneDate.error.message").toString(),
        });
      }
    },
    calculateImmobilizationDays(
      initDismantleMailDate: string | null | undefined,
      febValidationDate: string | null | undefined,
      dismantleDoneDate: string | null | undefined,
    ) {
      if (!febValidationDate || febValidationDate.trim().length == 0) {
        return this.$t("bop.immobilizationDays.unavailable");
      } else if (
        !initDismantleMailDate ||
        initDismantleMailDate.trim().length == 0
      ) {
        if (!dismantleDoneDate || dismantleDoneDate.trim().length == 0) {
          return `${diffDays(new Date(), new Date(febValidationDate))} ${this.$t("bop.immobilizationDays.days")}`;
        } else {
          return `${diffDays(new Date(dismantleDoneDate), new Date(febValidationDate))} ${this.$t("bop.immobilizationDays.days")}`;
        }
      } else {
        return `${diffDays(new Date(initDismantleMailDate), new Date(febValidationDate))} ${this.$t("bop.immobilizationDays.days")}`;
      }
    },
  },
  watch: {
    language() {
      (this as any).loadData();
    },
    hiddenColumns() {
      this.handleFilterChange();
      this.savePreferences();
    },
  },
  async created() {
    await (this as any).loadElementStatuses();
    await (this as any).loadBopTypes();
    await (this as any).loadData();
    await BopStatusService.getAllStatuses(this.$i18n.locale).then(
      (response) => {
        this.$store.commit("changeStatuses", response.data);
      },
    );
  },
});
</script>

<style scoped lang="scss">
#buttonHeader {
  display: inline-flex;
  flex-direction: row-reverse;

  // width: 100%;
  margin-bottom: 10px;
  button {
    width: 10rem;
    margin: 0 0.25rem;
  }
}
#resetFilters {
  display: inline-flex;
  flex-direction: row-reverse;
  width: 100%;
  margin-top: 10px;
}
#createBop {
  background-color: #003478;
  background: linear-gradient(90deg, #003478 80%, #809abc 20%);
  color: white;
  display: flex;
  flex-direction: row;
  position: right;
  padding-right: 0%;
  padding-left: 0%;
}

#createBop > div {
  box-sizing: border-box;
  margin-right: 10px;
  margin-left: 10px;
}

#arrow {
  text-align: right;
  margin-right: 10px;
}

:deep(.flatpickr-input.form-control[readonly]),
:deep(input.form-control[readonly]) {
  background-color: #fff !important;
  color: #495057 !important;
  opacity: 1;
}
.btn-column-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  width: 28px;
  padding: 0;
  flex-shrink: 0;
  font-weight: bold;
  border: none;
}
</style>
